import type { Label, UpdateLabelRequest } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../../components/generic/Modal';
import { useUpdateLabelMutation } from '../../../graphql/hooks/useUpdateLabelMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';
import { LabelForm } from './LabelForm';

type FormValues = UpdateLabelRequest;

interface Props {
  label: Label;
}

const schema: z.ZodSchema<FormValues> = z.object({
  name: z.string(),
  color: z.string(),
  description: z.string().nullish(),
});

export const LabelUpdateModal = createModal<Props>((props) => {
  const { label, modal } = props;
  const { organization, successToast, errorToast } = useAppContext();
  const { hide, remove, visible } = modal;

  const methods = useForm<FormValues>({
    defaultValues: label,
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const mutation = useUpdateLabelMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          labelId: label.id,
          request: values,
        })
        .then(() => {
          hide();
          successToast('Label updated successfully');
        })
        .catch((err) => {
          errorToast(`Error updating label: ${err.message}`);
        });
    },
    [errorToast, hide, mutation, organization.id, label.id, successToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Edit a label'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <LabelForm />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
